import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2Off, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

import { PinterestLogo } from './PinterestLogo';
import { usePinterestStatus } from './use-pinterest';

/**
 * Pinterest on the social accounts page: connect, "connected as @name",
 * disconnect — the same card shape as the publishing accounts beside it.
 *
 * It sits in its own "inspiration" group because it is not one of them:
 * Pinterest is read for Studio references and never posted to, so it takes no
 * account slot and does not appear where a post's destination is chosen. When
 * the server has no Pinterest app yet, the card says so calmly instead of
 * offering a button that would fail.
 */
export const PinterestConnectCard: React.FC<{ className?: string }> = ({ className }) => {
    const { t } = useTranslation();
    const pinterest = usePinterestStatus();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);

    const account = pinterest.status?.account;

    const connect = async () => {
        try {
            // Back to this tab, where the callback's banner is read.
            await pinterest.connect('/marketing/social-scheduler?tab=accounts');
        } catch {
            toast.error(t('PINTEREST.ERROR_CONNECT'));
        }
    };

    const disconnect = async () => {
        setDisconnecting(true);
        try {
            await pinterest.disconnect();
            toast.success(t('PINTEREST.DISCONNECTED_TOAST'));
            setConfirmOpen(false);
        } catch {
            toast.error(t('PINTEREST.ERROR_DISCONNECT'));
        } finally {
            setDisconnecting(false);
        }
    };

    if (pinterest.loading && !pinterest.status) {
        return (
            <div className={cn('rounded-xl border bg-card p-3.5', className)} aria-hidden>
                <div className="flex items-center gap-2">
                    <Skeleton className="size-7 rounded-full" />
                    <div className="flex flex-1 flex-col gap-1.5">
                        <Skeleton className="h-3.5 w-1/2" />
                        <Skeleton className="h-3 w-1/3" />
                    </div>
                </div>
            </div>
        );
    }

    const unconfigured = !pinterest.configured;
    const isConnected = pinterest.connected && Boolean(account);

    return (
        <>
            <div
                className={cn('flex flex-col gap-2.5 rounded-xl border bg-card p-3.5', unconfigured && 'opacity-80', className)}
                data-testid="pinterest-connect-card"
            >
                <div className="flex items-center gap-2">
                    <PinterestLogo size={28} />
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm leading-tight font-medium text-foreground" dir={isConnected ? 'ltr' : undefined}>
                            {account?.name || t('PINTEREST.TITLE')}
                        </div>
                        <div className="mt-0.5 text-xs leading-none text-muted-foreground">
                            {account ? t('PINTEREST.TITLE') : t('PINTEREST.CARD_DESC')}
                        </div>
                    </div>
                    {unconfigured && (
                        <span className="shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                            {t('PINTEREST.COMING_SOON')}
                        </span>
                    )}
                </div>

                {unconfigured ? (
                    <p className="text-xs leading-relaxed text-muted-foreground">{t('PINTEREST.CARD_UNCONFIGURED')}</p>
                ) : (
                    <>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span
                                aria-hidden
                                className={cn(
                                    'size-1.5 shrink-0 rounded-full',
                                    isConnected ? 'bg-success' : pinterest.expired ? 'bg-warning' : 'bg-muted-foreground/40',
                                )}
                            />
                            <span className="truncate">
                                {isConnected
                                    ? t('PINTEREST.CONNECTED_AS', { username: account!.username })
                                    : pinterest.expired
                                        ? t('PINTEREST.EXPIRED_TITLE')
                                        : t('PINTEREST.CARD_USE_IN_STUDIO')}
                            </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                            {!isConnected && (
                                <Button variant="outline" size="sm" onClick={connect} disabled={pinterest.redirecting}>
                                    {pinterest.redirecting ? <Loader2 className="animate-spin" /> : <Plus />}
                                    {pinterest.redirecting
                                        ? t('PINTEREST.CONNECTING')
                                        : pinterest.expired ? t('PINTEREST.RECONNECT') : t('PINTEREST.CONNECT')}
                                </Button>
                            )}
                            {account && (
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => setConfirmOpen(true)}
                                    className="-ms-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                >
                                    <Link2Off />
                                    {t('PINTEREST.DISCONNECT')}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <Dialog open={confirmOpen} onOpenChange={(next) => { if (!next && !disconnecting) setConfirmOpen(false); }}>
                <DialogContent className="sm:max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>{t('PINTEREST.DISCONNECT_CONFIRM_TITLE')}</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                        {t('PINTEREST.DISCONNECT_CONFIRM_BODY')}
                    </DialogDescription>
                    <DialogFooter>
                        <Button variant="outline" className="h-9" onClick={() => setConfirmOpen(false)} disabled={disconnecting}>
                            {t('PINTEREST.CANCEL')}
                        </Button>
                        <Button
                            variant="outline"
                            className="h-9 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={disconnect}
                            disabled={disconnecting}
                        >
                            {disconnecting && <Loader2 className="animate-spin" />}
                            {t('PINTEREST.DISCONNECT')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
